import React, { useState, useEffect, useRef } from 'react';
import ModalWrapper from './ModalWrapper';
import { initialCardParams } from '../../constants';
import { 
  FaSearch, 
  FaList, 
  FaTh, 
  FaSpinner, 
  FaChevronUp, 
  FaChevronDown, 
  FaFileImport, 
  FaImage 
} from "react-icons/fa";

export default function ImportModal({ isOpen, onClose, onImportCard, cardParams, setCardParams }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [showArtOnly, setShowArtOnly] = useState(false);

  const inputRef = useRef(null);
  
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults(null);
      setExpandedId(null);
      setShowArtOnly(false);
      
      if (window.innerWidth < 768) {
        setViewMode('table');
      } else {
        setViewMode('grid');
      }

      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setResults(null);
    setExpandedId(null);

    try {
      const res = await fetch(`/api/importer/importer_dropdown.php?name_startsWith=${encodeURIComponent(query)}`);
      const data = await res.json();
      setResults(data || []);
    } catch (err) {
      console.error("Search failed", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    const t = type ? type.toLowerCase() : '';
    if (t.includes('spell')) return 'icon-spell.webp';
    if (t.includes('weapon')) return 'icon-weapon.webp';
    if (t.includes('hero')) return 'hero-icon (1).webp';
    if (t.includes('location')) return 'location-icon.webp';
    return 'icon-minion.webp'; // Default
  };

const getCardImageSrc = (item) => {
    if (item.hearthcards === 1) {
      return item.preview_url;
    }
    if (showArtOnly) {
      return `/art/${item.cardblizzid}.jpg`;
    }
    if (item.is_bg === 1) {
      const suffix = item.premium === 1 ? '_triple' : '';
      return `https://art.hearthstonejson.com/v1/bgs/latest/enUS/256x/${item.cardblizzid}${suffix}.png`;
    }
    return `https://art.hearthstonejson.com/v1/render/latest/enUS/256x/${item.cardblizzid}.png`;
  };

const handleImageError = (e, item) => {
    if (item.hearthcards === 1) {
      e.target.onerror = null;
      e.target.src = '/images/mobile/adfasdf.webp';
      return;
    }
    if (showArtOnly) {
      if (e.target.src.includes('/art/')) {
        e.target.src = `https://art.hearthstonejson.com/v1/128x/${item.cardblizzid}.jpg`;
      } else {
        e.target.src = '/images/mobile/adfasdf.webp';
      }
    } else {
      if (e.target.src.includes('/art/')) {
        // Prevent infinite loop if fallback image is also missing
        e.target.src = '/images/mobile/adfasdf.webp';
      } else {
        e.target.src = `/art/${item.cardblizzid}.jpg`;
      }
    }
  };

  const executeImport = async (cardId, artOnly, item = null) => {
    try {
      // Reuse creator.js's established Hearthcards import path for full custom-card imports.
      if (!artOnly && item?.hearthcards === 1 && onImportCard) {
        await onImportCard(cardId);
        onClose();
        return;
      }

      const formData = new FormData();
      formData.append('cardname', cardId);
      formData.append('source', item?.hearthcards === 1 ? 'user' : 'game');

      const res = await fetch('/api/importer/importer_main.php', { method: 'POST', body: formData });
      const data = await res.json();

      let artFile = "";
      if (data.hearthcards === '1') {
        artFile = data.ext === 'png' ? `temp/${data.art}.png` : `cards/${data.art}.webp`;
      } else {
        artFile = `art/${data.art}.jpg`;
      }

      if (artOnly) {
        setCardParams(prev => ({
          ...prev,
          Art: { 
            File: artFile, 
            originalUrl: "", 
            x: null, 
            y: null, 
            scale: null,
            disableDropShadow: data.hearthcards === '1'
          }
        }));
      } else {
        let importedParams = {};
        if (data.json) {
          try {
            importedParams = JSON.parse(data.json);
          } catch (e) {
            console.warn("Failed to parse nested card JSON, falling back to database fields.");
          }
        }

        if (Object.keys(importedParams).length > 0) {
          setCardParams({
            ...initialCardParams,
            ...importedParams,
            Art: {
              File: artFile,
              originalUrl: importedParams.Art?.originalUrl || "",
              x: importedParams.Art?.X !== undefined ? Number(importedParams.Art.X) : (importedParams.Art?.x !== undefined ? Number(importedParams.Art.x) : null),
              y: importedParams.Art?.Y !== undefined ? Number(importedParams.Art.Y) : (importedParams.Art?.y !== undefined ? Number(importedParams.Art.y) : null),
              scale: importedParams.Art?.zoom !== undefined ? Number(importedParams.Art.zoom) / 100 : (importedParams.Art?.scale !== undefined ? Number(importedParams.Art.scale) : null),
            }
          });
        } else {
          let dualClass = "";
          let mainClass = data.class ? (data.class.charAt(0).toUpperCase() + data.class.slice(1)) : "Neutral";
          let banner = "";

          if (data.dualclass === 'kabal') { mainClass = "Neutral"; banner = "banner_triclass_0-1.webp"; }
          else if (data.dualclass === 'grimy_goons') { mainClass = "Neutral"; banner = "banner_triclass_0-2.webp"; }
          else if (data.dualclass === 'jade_lotus') { mainClass = "Neutral"; banner = "banner_triclass_0-3.webp"; }
          else if (data.dualclass) {
            dualClass = data.dualclass ? (data.dualclass.charAt(0).toUpperCase() + data.dualclass.slice(1)) : "Neutral";
          }

          let runes = { Slot1: "", Slot2: "", Slot3: "" };
          let runeIdx = 1;
          for (let i = 0; i < (data.cost_blood || 0); i++) runes[`Slot${runeIdx++}`] = 'CardRunes_DeathKnight_Blood.webp';
          for (let i = 0; i < (data.cost_frost || 0); i++) runes[`Slot${runeIdx++}`] = 'CardRunes_DeathKnight_Frost.webp';
          for (let i = 0; i < (data.cost_unholy || 0); i++) runes[`Slot${runeIdx++}`] = 'CardRunes_DeathKnight_Unholy.webp';

      // Read coordinates dynamically from response if present, otherwise default to null
      const incomingX = (data.art_x !== undefined && data.art_x !== null && data.art_x !== '') ? Number(data.art_x) : null;
      const incomingY = (data.art_y !== undefined && data.art_y !== null && data.art_y !== '') ? Number(data.art_y) : null;
      const incomingScale = (data.art_scale !== undefined && data.art_scale !== null && data.art_scale !== '') ? Number(data.art_scale) : null;

          setCardParams({
            ...initialCardParams,
            Type: data.type || "minion",
            Class: mainClass,
            DualClass: dualClass,
            Banner: data.tradeable === '1' ? "banner_tradeable.webp" : banner,
            RightBanner: data.dualclass === 'kabal' || data.dualclass === 'grimy_goons' || data.dualclass === 'jade_lotus' ? "right-banner-sc-us2.webp" : "",
            Rarity: data.rarity ? (data.rarity.charAt(0).toUpperCase() + data.rarity.slice(1)) : "Common",
            Title: data.name || "",
            Tribe: data.race || "",
            Cost: { Type: data.bgstars > 0 ? "BGStars" : "Mana", Value: data.bgstars > 0 ? data.bgstars : (data.cost || "") },
            Attack: data.attack || "",
            Health: data.health || "",
            Text: data.desc || "",
            Emblem: data.setname || "CATAIcon",
            Runes: runes,
            Art: { 
              File: artFile, 
              originalUrl: "", 
              x: incomingX !== null ? incomingX : 111, 
              y: incomingY !== null ? incomingY : 111, 
              scale: incomingScale !== null ? incomingScale : 1.14 
            }
          });
        }
      }
      onClose();
    } catch (err) {
      console.error("Import failed", err);
      alert("Failed to import card. See console for details.");
    }
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose} title="Import Database Card" icon="fa-download">
      <div className="modal-body d-flex flex-column overflow-hidden p-0" data-bs-theme="dark">
        
        {/* Search Bar */}
        <div className="p-3 border-bottom border-secondary bg-black bg-opacity-25">
          <div className="input-group">
            <input 
              ref={inputRef}
              type="text" 
              className="form-control bg-black text-white border-secondary" 
              placeholder="Search by card name, numeric ID, or 8-character card ID..." 
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
            <button className="btn btn-outline-info px-4 d-flex align-items-center" type="button" onClick={handleSearch}>
              <FaSearch className="me-2" /> Search
            </button>
          </div>
        </div>

        {/* Results Area */}
        <div className="p-3 flex-grow-1 m-0" style={{ overflowY: 'auto', minHeight: '500px' }}>
          
          {/* Controls Panel (One Line) */}
          {!loading && results !== null && results.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mb-3 text-nowrap w-100">
              
              {/* LEFT: Art Toggle */}
              <div className="d-flex align-items-center gap-2">
                <div className="form-check form-switch mb-0 d-flex align-items-center gap-2 m-0 p-0">
                  <input
                    className="form-check-input pointer m-0"
                    type="checkbox"
                    id="showArtCheckbox"
                    checked={showArtOnly}
                    onChange={(e) => setShowArtOnly(e.target.checked)}
                  />
                  <label className="form-check-label text-white small pointer unselectable mb-0" htmlFor="showArtCheckbox">
                    Show Art Only
                  </label>
                </div>
                <div className="text-muted small d-none d-sm-block ms-2">
                  ({results.length} found)
                </div>
              </div>

              {/* RIGHT: Grid vs Table Switcher */}
              <div className="btn-group btn-group-sm flex-shrink-0">
                <button 
                  className={`btn px-3 py-1 ${viewMode === 'table' ? 'btn-warning text-dark' : 'btn-outline-secondary text-white'}`} 
                  onClick={() => setViewMode('table')}
                  title="Table View"
                >
                  <FaList size={16} />
                </button>
                <button 
                  className={`btn px-3 py-1 ${viewMode === 'grid' ? 'btn-warning text-dark' : 'btn-outline-secondary text-white'}`} 
                  onClick={() => setViewMode('grid')}
                  title="Grid View"
                >
                  <FaTh size={16} />
                </button>
              </div>

            </div>
          )}

          {loading && (
            <div className="text-center py-5">
              <FaSpinner className="fa-spin text-info" size={40} />
              <div className="mt-3 text-muted">Searching database...</div>
            </div>
          )}

          {!loading && results === null && (
            <div className="text-center text-muted align-self-center py-5">
              <FaSearch className="text-info mb-3 opacity-50" size={40} />
              <div>Search for an existing database card to import...</div>
            </div>
          )}

          {!loading && results !== null && results.length === 0 && (
            <div className="text-center text-muted py-5">No cards found.</div>
          )}

          {/* Results Lists */}
          {!loading && results?.length > 0 && (
            <>
              {/* --- TABLE / ACCORDION VIEW --- */}
              {viewMode === 'table' && (
                <div>
                  {results.map(item => {
                    const isExpanded = expandedId === item.cardid;
                    const tileUrl = item.hearthcards === 1
                      ? item.preview_url
                      : item.cardblizzid 
                      ? `https://art.hearthstonejson.com/v1/tiles/${item.cardblizzid}.webp` 
                      : '';

                    // Styling applied to the header row to handle the tile background art and standard shading
                    const headerStyle = {
                      backgroundImage: tileUrl 
                        ? `linear-gradient(90deg, rgba(0, 0, 0, 1) 30%, rgba(0, 0, 0, 0.45) 70%, rgba(0, 0, 0, 0.1) 100%), url(${tileUrl})` 
                        : 'none',
                      backgroundPosition: 'right center',
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat',
                      minHeight: '48px',
                      boxShadow: 'rgb(0, 0, 0) 0px 3px 5px 1px inset, rgb(0, 0, 0) 0px 0px 3px',
                    };

                    return (
                      <div key={`table-${item.cardid}`} className="mb-2 importer-submenu border border-secondary rounded shadow-sm overflow-hidden" style={{ backgroundColor: isExpanded ? 'var(--bg-panel)' : 'var(--bg-input)', transition: '0.2s' }}>
                        
                        {/* Header Row */}
                        <div 
                          className="d-flex align-items-center p-2 pointer" 
                          onClick={() => setExpandedId(isExpanded ? null : item.cardid)}
                          style={headerStyle}
                        >
                          <div className="d-flex align-items-center gap-2 me-3 flex-shrink-0">
                            <img src={`/images/mobile/${getTypeIcon(item.cardtype)}`} style={{ width: 30, height: 30, objectFit: 'contain', filter: 'brightness(1.5)' }} alt="Type" title={item.cardtype} />
                            <img src={`/images/selectorClasses/${item.classcolor}-64.png`} style={{ width: 30, height: 30, objectFit: 'contain' }} alt="Class" title={item.classcolor} />
                            <img src={`/images/selectorEmblems/${item.emblem}.png`} style={{ width: 30, height: 30, objectFit: 'contain', filter: 'invert(1)' }} alt="Emblem" title={item.emblem} />
                          </div>
                          <div 
                            className="flex-grow-1 text-white fw-bold text-truncate"
                            style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.9)' }}
                          >
                            {item.cardname}
                          </div>
                          {isExpanded ? (
                            <FaChevronUp className="text-white ms-2" style={{ filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.8))' }} />
                          ) : (
                            <FaChevronDown className="text-white ms-2" style={{ filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.8))' }} />
                          )}
                        </div>

                        {/* Accordion Body */}
                        {isExpanded && (
                          <div className="d-flex flex-row p-2 p-md-3 border-top border-secondary bg-black bg-opacity-25 gap-2 gap-md-3">
                            <div className="text-center flex-shrink-0" style={{ width: '40%', minWidth: '110px', maxWidth: '200px', aspectRatio: showArtOnly ? '1/1' : 'auto' }}>
                              <img 
                                src={getCardImageSrc(item)} 
                                alt="Preview" 
                                style={{ 
                                  width: '100%', 
                                  height: showArtOnly ? '100%' : 'auto', 
                                  maxHeight: '250px', 
                                  objectFit: showArtOnly ? 'cover' : 'contain' 
                                }}
                                onError={(e) => handleImageError(e, item)} 
                              />
                            </div>
                            <div className="d-flex flex-column justify-content-center flex-grow-1" style={{ minWidth: 0 }}>
                              <div className="mb-2 mb-md-3 text-muted" style={{ fontSize: '0.85rem' }}>
                                <div className="text-truncate"><strong>Type:</strong> <span className="text-white">{item.cardtype}</span></div>
                                <div className="text-truncate"><strong>Class:</strong> <span className="text-white text-capitalize">{item.classcolor}</span></div>
                                <div className="text-truncate"><strong>Set:</strong> <span className="text-white">{item.emblem}</span></div>
                              </div>
                              <div className="d-flex flex-column gap-2 mt-auto">
                                <button className="btn btn-warning fw-bold text-dark px-2 py-1 py-md-2 d-flex align-items-center justify-content-center" style={{ fontSize: '0.85rem' }} onClick={() => executeImport(item.cardid, false, item)}>
                                  <FaFileImport className="me-2" /> Import Full Card
                                </button>
                                <button className="btn btn-outline-info px-2 py-1 py-md-2 d-flex align-items-center justify-content-center" style={{ fontSize: '0.85rem' }} onClick={() => executeImport(item.cardid, true, item)}>
                                  <FaImage className="me-2" /> Import Art Only
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* --- GRID VIEW --- */}
              {viewMode === 'grid' && (
                <div className="row row-cols-2 row-cols-sm-3 row-cols-md-4 g-2 g-md-3">
                  {results.map(item => (
                    <div className="col" key={`grid-${item.cardid}`}>
                      <div className="importer-submenu d-flex flex-column h-100 border border-secondary rounded shadow-sm overflow-hidden" style={{ backgroundColor: 'var(--bg-panel)' }}>
                        
                        {/* Grid Item Header */}
                        <div className="p-2 bg-black bg-opacity-25 border-bottom border-secondary text-center">
                          <div className="text-white fw-bold text-truncate" style={{ fontSize: '0.85rem' }} title={item.cardname}>
                            {item.cardname}
                          </div>
                        </div>

                        {/* Image Frame */}
                        <div 
                          className="text-center bg-black bg-opacity-50 overflow-hidden position-relative w-100" 
                          style={showArtOnly ? { aspectRatio: '1/1' } : {}}
                        >
                          <img 
                            src={getCardImageSrc(item)} 
                            alt="Preview" 
                            className="w-100 h-100"
                            style={{ 
                              objectFit: showArtOnly ? 'cover' : 'contain', 
                              height: showArtOnly ? '100%' : 'auto', 
                              display: 'block' 
                            }}
                            onError={(e) => handleImageError(e, item)} 
                          />
                        </div>

                        {/* Card Actions & Details Block */}
                        <div className="p-2 d-flex flex-column flex-grow-1 border-top border-secondary">
                          
                          {/* Buttons in one line split in half */}
                          <div className="d-flex gap-1 mb-2">
                            <button className="btn btn-warning fw-bold text-dark py-1 px-1 flex-grow-1 w-50 text-truncate d-flex align-items-center justify-content-center" style={{ fontSize: '0.75rem' }} onClick={() => executeImport(item.cardid, false, item)}>
                              <FaFileImport className="me-1" /> Full
                            </button>
                            <button className="btn btn-outline-info py-1 px-1 flex-grow-1 w-50 text-truncate d-flex align-items-center justify-content-center" style={{ fontSize: '0.75rem' }} onClick={() => executeImport(item.cardid, true, item)}>
                              <FaImage className="me-1" /> Art
                            </button>
                          </div>

                          {/* Inline Metadata Icons Row */}
                          <div className="d-flex justify-content-center align-items-center gap-3 mt-auto pt-2 border-top border-secondary border-opacity-25">
                            <img 
                              src={`/images/mobile/${getTypeIcon(item.cardtype)}`} 
                              style={{ width: 22, height: 22, objectFit: 'contain', filter: 'brightness(1.5)' }} 
                              alt="Type" 
                              title={`Type: ${item.cardtype}`} 
                            />
                            <img 
                              src={`/images/selectorClasses/${item.classcolor}-64.png`} 
                              style={{ width: 22, height: 22, objectFit: 'contain' }} 
                              alt="Class" 
                              title={`Class: ${item.classcolor}`} 
                            />
                            <img 
                              src={`/images/selectorEmblems/${item.emblem}.png`} 
                              style={{ width: 22, height: 22, objectFit: 'contain', filter: 'invert(1)' }} 
                              alt="Emblem" 
                              title={`Set: ${item.emblem}`} 
                            />
                          </div>

                        </div>

                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

        </div>
      </div>
    </ModalWrapper>
  );
}
